// Postprocessor plug-in contract.
//
// Each shop / machine vendor has its own native format; the contract here
// is intentionally narrow so adding e.g. Cabinet Vision (CV5), eCabinets,
// Microvellum, IMOS, raw G-code, or DXF flavours later is just "drop in a
// new module that exports a Postprocessor". The UI picks them up from the
// registry.

import type {
  Cabinet,
  CabinetPart,
  Project,
} from "@/lib/types";
import type { CatalogIndex } from "@/lib/cabinets/bom";
import type { ProjectNesting } from "@/lib/cabinets/nest";
import type { ProjectBom } from "@/lib/cabinets/bom";

/** Sources every postprocessor can read from when generating its output. */
export interface ExportContext {
  project:  Project;
  cabinets: Cabinet[];
  /** Pre-resolved parts per cabinet (materialId already set). */
  parts:    Array<{ cabinet: Cabinet; parts: CabinetPart[] }>;
  catalog:  CatalogIndex;
  bom:      ProjectBom;
  nesting:  ProjectNesting;
}

/** One file produced by a postprocessor. */
export interface ExportArtifact {
  filename: string;
  /** `string` for ASCII formats (CSV/DXF/MPR), `Uint8Array` for binary. */
  content:  string | Uint8Array;
  /** RFC-7231 media type — drives the `Content-Type` of the downloaded blob. */
  mimeType: string;
}

/** Category drives UI grouping in <ExportPanel>. */
export type PostprocessorCategory =
  | "cut_list"       // saw / panel saw
  | "drill"          // CNC drill (32mm system, hinge cups)
  | "drawing"        // 2D CAD (DXF, DWG-as-DXF, SVG)
  | "machine";       // proprietary native format (MPR, eCab, IMOS)

export interface PostprocessorOption {
  key:      string;
  label:    string;
  type:     "select" | "number" | "boolean";
  default:  string | number | boolean;
  /** For "select" only. */
  choices?: { value: string; label: string }[];
  /** Optional inline help. */
  help?:    string;
}

export interface Postprocessor {
  id:          string;
  label:       string;
  description: string;
  category:    PostprocessorCategory;
  /** File extension shown in the UI label; not authoritative for naming
   *  (the artifact carries its own filename). */
  extension:   string;
  /** Knobs surfaced as form fields in <ExportPanel>. */
  options:     PostprocessorOption[];
  /** Pure function: read ctx + opts, return one-or-more artifacts.
   *  May throw on unsupported input — caller surfaces the message. */
  generate(ctx: ExportContext, opts: Record<string, string | number | boolean>): ExportArtifact[];
}

/** Resolved options for one run, keyed by option.key. */
export type OptionValues = Record<string, string | number | boolean>;

export function defaultOptions(pp: Postprocessor): OptionValues {
  const out: OptionValues = {};
  for (const o of pp.options) out[o.key] = o.default;
  return out;
}
